import type { ApiKeyType } from "@equidefi/constants/openapi";
import type { DTO } from "@equidefi/shared/types";
import { ApiClient } from "./ApiClient";

export type TrancheAgreementsStatus = {
  status?: "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED";
  error?: string | null;
  url?: string | null;
  created_at?: string;
};

class OfferingClient extends ApiClient {
  async search(params: { search?: string; page?: number }) {
    const response = await this.get("/v2/offerings", { params });
    return response.data;
  }

  async investments(id: string, filters = {}) {
    if (!id) {
      return null;
    }

    const response = await this.get(`/v2/offerings/${id}/investments`, {
      params: filters,
    });
    return response.data;
  }

  async listCountersigners(offeringId: string): Promise<DTO.User[]> {
    const response = await this.get(`/v2/offerings/${offeringId}/countersigners`);
    return response.data;
  }

  async addCountersigner(offeringId: string, userId: string) {
    const response = await this.post(`/v2/offerings/${offeringId}/countersigners`, {
      user_id: userId,
    });
    return response.data;
  }

  async removeCountersigner(offeringId: string, userId: string) {
    const response = await this.delete(`/v2/offerings/${offeringId}/countersigners/${userId}`);
    return response.data;
  }

  async apiKey(id: string, type?: ApiKeyType) {
    const response = await this.get(`/v2/offerings/${id}/key`, {
      params: type ? { type } : undefined,
    });
    return response.data;
  }

  async setupApiKey(id: string, type?: ApiKeyType) {
    const response = await this.post(`/v2/offerings/${id}/key`, type ? { type } : undefined);
    return response.data;
  }

  async rotateApiKey(id: string, type?: ApiKeyType) {
    const response = await this.post(`/v2/offerings/${id}/key/rotate`, type ? { type } : undefined);
    return response.data;
  }

  async legacyCreate(data: unknown) {
    const response = await this.post("/offering", data);
    return response.data;
  }

  async legacyFind(id: string) {
    const response = await this.get(`/offering/${id}`);
    return response.data;
  }

  async getFullById(id: string) {
    const response = await this.get(`/v2/offerings/${id}/full`);
    return response.data ?? {};
  }

  async getOfferingDocuments(offeringId: string) {
    const response = await this.get(`/v2/offerings/${offeringId}/documents`);
    return response.data;
  }

  async createOfferingDocument(offeringId: string, data: object) {
    const response = await this.post(`/v2/offerings/${offeringId}/documents`, data);
    return response.data;
  }

  async updateOfferingDocument(offeringId: string, documentId: string, data: object) {
    const response = await this.patch(`/v2/offerings/${offeringId}/documents/${documentId}`, data);
    return response.data;
  }

  async deleteOfferingDocument(offeringId: string, documentId: string, data: object) {
    const response = await this.delete(`/v2/offerings/${offeringId}/documents/${documentId}`, data);
    return response.data;
  }

  async getById(id: string) {
    const response = await this.get(`/v2/offerings/${id}`);
    return response.data?.data;
  }

  async update(id: string, data: Partial<DTO.Offering>) {
    const response = await this.patch(`/v2/offerings/${id}`, data);
    return response.data;
  }

  async remove(id: string) {
    const response = await this.delete(`/v2/offerings/${id}`);
    return response.data;
  }

  async summary(id: string) {
    const response = await this.get(`/v2/offerings/${id}/summary`);
    return response.data;
  }

  async settings(id: string) {
    const response = await this.get<DTO.Offering["settings"]>(`/v2/offerings/${id}/settings`);
    return response.data;
  }

  async updateSettings(id: string, data: object) {
    const response = await this.post(`/v2/offerings/${id}/settings`, data);
    return response.data;
  }

  async brokers(id: string) {
    const response = await this.get(`/v2/offerings/${id}/brokers`);
    return response.data;
  }

  async addBroker(offeringId: string, brokerId: string) {
    const response = await this.post(`/v2/offerings/${offeringId}/broker/${brokerId}`);
    return response.data;
  }

  async removeBroker(offeringId: string, brokerId: string) {
    const response = await this.delete(`/v2/offerings/${offeringId}/broker/${brokerId}`);
    return response.data;
  }

  async getTranches(offeringId: string, filters: DTO.TrancheFilters = {}) {
    const response = await this.get(`/v2/offerings/${offeringId}/traunches`, {
      params: filters,
    });
    return response.data;
  }

  async getTranchesAggregations(offeringId: string) {
    const response = await this.get(`/v2/offerings/${offeringId}/traunches/aggregations`);
    return response.data;
  }

  async getAvailableInvestments(offeringId: string, filters: DTO.TrancheFilters) {
    const response = await this.get(`/v2/offerings/${offeringId}/traunches/investments`, {
      params: filters,
    });
    return response.data;
  }

  async getTranche(id: string, offeringId: string) {
    const response = await this.get(`/v2/offerings/${offeringId}/traunches/${id}`);
    return response.data;
  }

  async getTrancheInvestments(id: string, offeringId: string, filters: DTO.TrancheFilters) {
    const response = await this.get(`/v2/offerings/${offeringId}/tranche-investments/${id}`, {
      params: filters,
    });
    return response.data;
  }

  async createTranche(offeringId: string) {
    const response = await this.post(`/v2/offerings/${offeringId}/traunches`);
    return response.data;
  }

  async getTrancheDocument(id: string, offeringId: string, document: string) {
    const response = await this.get(`/v2/offerings/${offeringId}/traunches/${id}/document/${document}`);

    return response.data;
  }

  async authenticate(email: string, password: string, offeringId: string) {
    const response = await this.post(`/v2/offerings/${offeringId}/auth`, {
      email,
      password,
    });
    return response.data;
  }

  async register(data: Record<string, any>, offeringId: string) {
    const response = await this.post(`/v2/offerings/${offeringId}/register`, data);
    return response;
  }

  async issuerOfferings(issuerId: string, params: Record<"page" | "search", string>) {
    const response = await this.get(`/v2/offerings/issuer/${issuerId}`, {
      params,
    });
    return response.data;
  }

  async userOfferings() {
    const response = await this.get("/v2/offerings/user");
    return response.data;
  }

  async exportInvestors(offeringId: string, data: object) {
    const response = await this.post(`/v2/offerings/${offeringId}/export`, data);
    return response;
  }

  async offeringContacts(offeringId: string) {
    const response = await this.get(`/v2/offerings/${offeringId}/contacts`);
    return response.data;
  }

  async createOfferingContact(offeringId: string, data: object) {
    const response = await this.post(`/v2/offerings/${offeringId}/contacts`, data);
    return response.data;
  }

  async updateOfferingContact(offeringId: string, data: object) {
    // @ts-ignore
    const response = await this.patch(`/v2/offerings/${offeringId}/contacts/${data?.id}`, data);
    return response.data;
  }

  async deleteOfferingContact(offeringId: string, contactId?: string) {
    if (!contactId) return null;
    const response = await this.delete(`/v2/offerings/${offeringId}/contacts/${contactId}`);
    return response.data;
  }

  async createOffering(issuerId: string, data: object) {
    const response = await this.post("/v2/offerings/create", {
      issuer_id: issuerId,
      ...data,
    });
    return response;
  }

  async getAggregations(offeringId: string) {
    const response = await this.get(`/v2/offerings/${offeringId}/aggregations`);
    return response.data;
  }

  async getPlatformSettings() {
    const response = await this.get("/v2/offerings/platform/settings");
    return response.data;
  }

  async getOfferingPayments(offeringId: string, filters: object) {
    const response = await this.get(`/v2/offerings/${offeringId}/payments`, {
      params: filters,
    });
    return response.data;
  }

  async getOfferingPayouts(offeringId: string, params: { page?: number } = {}) {
    const response = await this.get(`/v2/offerings/${offeringId}/payouts`, {
      params,
    });
    return response.data;
  }

  async exportOfferingPayouts(offeringId: string) {
    const response = await this.post(`/v2/offerings/${offeringId}/payouts/export`);
    return response;
  }

  async getOfferingPayout(offeringId: string, payoutId: string, params: { page?: number } = {}) {
    const response = await this.get(`/v2/offerings/${offeringId}/payouts/${payoutId}`, {
      params,
    });
    return response.data;
  }

  async getPaymentsAggregations(offeringId: string) {
    const response = await this.get(`/v2/offerings/${offeringId}/payments/aggregations`);
    return response.data;
  }

  async generateClosingDocument(id: string, offeringId: string, document: string) {
    const response = await this.post(`/v2/offerings/${offeringId}/traunches/${id}/document/generate`, { document });

    return response.data;
  }

  async downloadTrancheAgreements(id: string, offeringId: string) {
    const response = await this.post(`/v2/offerings/${offeringId}/traunches/${id}/agreements/download`);

    return response.data;
  }

  async getTrancheAgreementsStatus(id: string, offeringId: string): Promise<TrancheAgreementsStatus> {
    const response = await this.get(`/v2/offerings/${offeringId}/traunches/${id}/agreements/status`);

    return response.data;
  }

  async triggerBulkCountersign(
    offeringId: string,
    selection: { all: boolean; ids: string[]; excluded: string[] },
  ): Promise<DTO.BulkCountersignTrigger> {
    const response = await this.post(`/v2/offerings/${offeringId}/countersign`, selection);
    return response.data;
  }

  async getBulkCountersignStatus(offeringId: string): Promise<DTO.BulkCountersignStatus> {
    const response = await this.get(`/v2/offerings/${offeringId}/countersign/status`);
    return response.data;
  }

  async getPreClosingInvestments(offeringId: string, filters: object): Promise<DTO.PreClosingData> {
    const response = await this.get(`/v2/offerings/${offeringId}/pre-closing`, {
      params: filters,
    });
    return response.data;
  }

  async getPreClosingAggregations(offeringId: string, filters: object): Promise<DTO.PreClosingAggregations> {
    const response = await this.get(`/v2/offerings/${offeringId}/pre-closing/aggregations`, {
      params: filters,
    });
    return response.data;
  }

  async createOfferingHistoryLog(
    offeringId: string,
    data: {
      category: DTO.OfferingHistoryCategory;
      description: string;
      title?: string;
    },
  ): Promise<DTO.PreClosingData> {
    const response = await this.post(`/v2/offerings/${offeringId}/history`, data);
    return response.data;
  }
}

export default OfferingClient;
